/**
 * useQuestLog — Mission-based onboarding system.
 * Replaces passive 8-slide briefing with sequential quests
 * that auto-detect completion as the user performs actions.
 * Persists progress to localStorage so users can resume.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { FEES, WAR } from "@shared/constants";

// ---------------------------------------------------------------------------
// Quest definitions
// ---------------------------------------------------------------------------

export interface Quest {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  /** Expandable bullet-point explainer shown in QuestHUD Intel section */
  briefing: string[];
  icon: string;
  reward: string;
  spotlightTarget?: string;
  spotlightHint?: string;
  check: (ctx: QuestContext) => boolean;
}

export interface QuestContext {
  hasNation: boolean;
  currentPage: string;
  activeTab: string | null;
  commandPaletteOpened: boolean;
}

const QUESTS: Quest[] = [
  {
    id: "enlist",
    title: "ENLIST",
    subtitle: "Join a Nation",
    description: "Pick a nation to pledge allegiance \u2014 this is your team.",
    briefing: [
      "Each nation has a unique DOCTRINE \u2014 a permanent bonus that shapes its strategy.",
      "\uD83C\uDDFA\uD83C\uDDF8 USA: Military-Industrial (+20% unit power, +10% steel)",
      "\uD83C\uDDE8\uD83C\uDDF3 CHN: Manufacturing Hub (+25% all production, \u201310% unit power)",
      "\uD83C\uDDF7\uD83C\uDDFA RUS: Nuclear Doctrine (+30% uranium, +20% nuke power)",
      "\uD83C\uDDEF\uD83C\uDDF5 JPN: Technological (+30% tech, +20% cyber power)",
      "\uD83C\uDDEE\uD83C\uDDF1 ISR: Intelligence State (+30% espionage, +25% counter-intel)",
      "\uD83C\uDDE9\uD83C\uDDEA DEU: Engineering (+20% building efficiency, +15% research speed)",
      "\uD83C\uDDEC\uD83C\uDDE7 GBR: Naval Supremacy (+30% navy power, +15% spy defense)",
      "\uD83C\uDDEB\uD83C\uDDF7 FRA: Diplomatic Corps (+25% alliance revenue, +15% morale)",
      "\uD83C\uDDE7\uD83C\uDDF7 BRA: Agricultural (+25% food, +15% population growth)",
      "\uD83C\uDDEE\uD83C\uDDF3 IND: Population Power (+20% pop cap, +25% headcount bonus)",
      "\uD83C\uDDF0\uD83C\uDDF7 KOR: Cyber Warfare (+30% cyber power, +20% espionage)",
      "\uD83C\uDDE6\uD83C\uDDFA AUS: Resource Rich (+20% gold & oil production)",
      "Pick based on your play style: fight? USA. Economy? CHN. Spy? ISR. Tech? JPN.",
      "You CAN switch nations later (defection) but it costs 10% of your stake + 72hr penalty.",
    ],
    icon: "\uD83C\uDFF4",
    reward: "Nation intel unlocked",
    spotlightTarget: "nation-select",
    spotlightHint: "Click any flag to join that nation",
    check: (ctx) => ctx.hasNation,
  },
  {
    id: "recon",
    title: "RECON",
    subtitle: "View Your Nation",
    description: "Open your nation\u2019s detail page to see resources, military, and citizens.",
    briefing: [
      "The Nation page shows your RESOURCES: Gold, Oil, Tech, Food, Steel, Uranium.",
      "Resources are produced by buildings every 5 seconds (1 simulation tick).",
      "MILITARY POWER = sum of all your units\u2019 power. More power = win more wars.",
      "MORALE (0\u2013100%): High morale boosts combat. Drops on war losses, rises on wins.",
      "TREASURY: Your war fund, fueled by coin trade fees. Enemies drain this when they attack!",
      "CITIZENS count determines voting power and the headcount bonus in wars.",
    ],
    icon: "\uD83D\uDD0D",
    reward: "Strategic overview unlocked",
    spotlightTarget: "view-nation",
    spotlightHint: "Click here to inspect your nation",
    check: (ctx) => ctx.currentPage === "nation",
  },
  {
    id: "intel",
    title: "INTEL",
    subtitle: "Check the War Map",
    description: "The War Map shows all 12 nations, active wars, and alliances.",
    briefing: [
      "The WAR MAP shows all 12 nations positioned on a world map.",
      "Red lines = active wars. Amber = nuke countdowns. Green = alliances.",
      "STATS TICKER (top bar): Nations count, Active Ops, Total Treasury, Biggest Treasury.",
      "SIDEBAR (left): Threat level, all nations with morale bars, active operations list.",
      "Click any nation on the map to see detailed intel \u2014 resources, military, win/loss record.",
    ],
    icon: "\uD83D\uDDFA\uFE0F",
    reward: "Theater of operations visible",
    spotlightTarget: "tab-war_map",
    spotlightHint: "Click the War Map tab",
    check: (ctx) => ctx.currentPage === "dashboard" && ctx.activeTab === "war_map",
  },
  {
    id: "monitor",
    title: "MONITOR",
    subtitle: "Watch the Battle Feed",
    description: "The Battle Feed streams live war events \u2014 duels, strikes, and nukes.",
    briefing: [
      "3 WAR TYPES with escalating stakes:",
      "DUEL: Free 1v1. Rock-paper-scissors (Land > Sea > Air > Land). Best of 3. No treasury drain.",
      `STRIKE: Costs ${FEES.STRIKE_PROPOSE_USD.toFixed(0)} Credits. Needs ${WAR.STRIKE_QUORUM * 100}% citizen quorum. Drains ${WAR.STRIKE_MAX_DRAIN * 100}% of defender\u2019s treasury.`,
      `NUKE: Costs ${FEES.NUKE_PROPOSE_USD.toFixed(0)} Credits. Needs ${WAR.NUKE_QUORUM * 100}% quorum. ${WAR.NUKE_COUNTDOWN_HOURS}-hour countdown. Drains ${WAR.NUKE_MAX_DRAIN * 100}%! 50% goes to a random nation.`,
      `If your attack FAILS: lose ${WAR.FAILED_STRIKE_PENALTY * 100}% of committed stake (strike) or ${WAR.FAILED_NUKE_PENALTY * 100}% (nuke). High risk, high reward.`,
      "SHIELDS: Post-nuke 7-day immunity. Underdogs get 1.5\u20132x defense bonus automatically.",
    ],
    icon: "\u2694\uFE0F",
    reward: "Battle awareness online",
    spotlightTarget: "tab-battle_feed",
    spotlightHint: "Click Battle Feed to watch live wars",
    check: (ctx) => ctx.currentPage === "dashboard" && ctx.activeTab === "battle_feed",
  },
  {
    id: "deploy",
    title: "DEPLOY",
    subtitle: "Visit the Agents Tab",
    description: "AI agents can fight for your nation autonomously via API.",
    briefing: [
      "AI AGENTS play the game for you 24/7 \u2014 building, recruiting, researching, waging war.",
      "Register via API: POST /api/agent/register with a name, strategy, and nation code.",
      "Your agent gets an API key to: build structures, recruit troops, start research, propose wars.",
      "Strategies: \u2018aggressive\u2019 (attack), \u2018defensive\u2019 (build & defend), \u2018economic\u2019 (maximize resources).",
      "Agents + humans together = strongest nations. The bot grinds, you make the big calls.",
    ],
    icon: "\uD83E\uDD16",
    reward: "Agent deployment ready",
    spotlightTarget: "tab-agents",
    spotlightHint: "Click Agents to learn about AI bots",
    check: (ctx) => ctx.currentPage === "dashboard" && ctx.activeTab === "agents",
  },
  {
    id: "command",
    title: "COMMAND",
    subtitle: "Open the Terminal",
    description: "Press / anywhere to open the command terminal for quick navigation.",
    briefing: [
      "The COMMAND TERMINAL (/) gives you quick access to everything:",
      "Search nations, jump to tabs, start wars, view docs \u2014 all without clicking menus.",
      "Type \u2018tutorial\u2019 to replay the full briefing. Type a nation code to jump to its page.",
      "Power users live in the terminal. It\u2019s your shortcut to every action in the game.",
    ],
    icon: "\u2328\uFE0F",
    reward: "Full operator access granted",
    // No spotlight \u2014 keyboard overlay instead
    check: (ctx) => ctx.commandPaletteOpened,
  },
];

// ---------------------------------------------------------------------------
// Persistence keys
// ---------------------------------------------------------------------------

const PROGRESS_KEY = "cw_quest_progress";
const DISMISSED_KEY = "cw_quest_dismissed";
const MINIMIZED_KEY = "cw_quest_minimized";

function loadProgress(): string[] {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveProgress(ids: string[]) {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(ids));
  } catch {
    // ignore
  }
}

function loadDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "true";
  } catch {
    return false;
  }
}

function loadMinimized(): boolean {
  try {
    const raw = localStorage.getItem(MINIMIZED_KEY);
    if (raw !== null) {
      return raw === "true";
    }

    return window.matchMedia("(max-width: 767px)").matches;
  } catch {
    return false;
  }
}

function saveMinimized(next: boolean) {
  try {
    localStorage.setItem(MINIMIZED_KEY, String(next));
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface QuestLogState {
  /** "welcome" = show welcome card; "active" = quests running; "complete" = all done */
  phase: "welcome" | "active" | "complete" | "dismissed";
  activeQuest: Quest | null;
  completedIds: string[];
  totalQuests: number;
  progress: number;
  isMinimized: boolean;
  /** True for ~1.5s after a quest completes (drives celebration animation) */
  celebrationActive: boolean;
  /** The reward text of the last completed quest (shown during celebration) */
  lastCompletedReward: string | null;
  dismissWelcome: () => void;
  skipAll: () => void;
  updateContext: (ctx: Partial<QuestContext>) => void;
  toggleMinimize: () => void;
  dismiss: () => void;
}

export function useQuestLog(isAuthed: boolean): QuestLogState {
  const [completedIds, setCompletedIds] = useState<string[]>(loadProgress);
  const [dismissed, setDismissed] = useState(loadDismissed);
  const [welcomeShown, setWelcomeShown] = useState(false);
  const [isMinimized, setIsMinimized] = useState(loadMinimized);
  const [celebrationActive, setCelebrationActive] = useState(false);
  const [lastCompletedReward, setLastCompletedReward] = useState<string | null>(null);

  // Context ref (avoids re-renders on every context update)
  const contextRef = useRef<QuestContext>({
    hasNation: false,
    currentPage: "dashboard",
    activeTab: "war_map",
    commandPaletteOpened: false,
  });

  // Track whether we've already started the quest so "intel" quest doesn't
  // auto-complete if user is already on war_map tab when it activates
  const questActivatedRef = useRef<string | null>(null);
  const tabAtActivationRef = useRef<string | null>(null);
  const tabQuestNeedsSwitchRef = useRef(false);

  // Derive phase
  const allComplete = completedIds.length >= QUESTS.length;
  const hasAnyProgress = completedIds.length > 0;

  let phase: "welcome" | "active" | "complete" | "dismissed";
  if (dismissed) {
    phase = "dismissed";
  } else if (!isAuthed) {
    phase = "dismissed"; // hide until authed
  } else if (allComplete) {
    phase = "complete";
  } else if (!hasAnyProgress && !welcomeShown) {
    phase = "welcome";
  } else {
    phase = "active";
  }

  // Find the first incomplete quest
  const activeQuest = QUESTS.find((q) => !completedIds.includes(q.id)) ?? null;

  // Track quest activation to prevent premature completion
  useEffect(() => {
    if (activeQuest && activeQuest.id !== questActivatedRef.current) {
      questActivatedRef.current = activeQuest.id;
      tabAtActivationRef.current = contextRef.current.activeTab;

      const targetTab = activeQuest.spotlightTarget?.startsWith("tab-")
        ? activeQuest.spotlightTarget.slice(4)
        : null;

      // If a tab quest activates while already on its target tab,
      // require one tab change away before allowing completion.
      tabQuestNeedsSwitchRef.current = !!targetTab && targetTab === contextRef.current.activeTab;
    }
  }, [activeQuest]);

  // Auto-detect quest completion
  const checkCompletion = useCallback(() => {
    if (!activeQuest || celebrationActive) return;
    const ctx = contextRef.current;

    // For tab-based quests (intel, monitor, deploy), don't auto-complete if
    // the quest activated while already on its target tab. Once the user
    // switches away from that tab, completion is allowed when they return.
    if (activeQuest.spotlightTarget?.startsWith("tab-") && activeQuest.id === questActivatedRef.current) {
      if (tabQuestNeedsSwitchRef.current) {
        if (ctx.activeTab === tabAtActivationRef.current) {
          return;
        }
        tabQuestNeedsSwitchRef.current = false;
      }
    }

    if (activeQuest.check(ctx)) {
      // Fire celebration
      setCelebrationActive(true);
      setLastCompletedReward(activeQuest.reward);
      setTimeout(() => {
        setCompletedIds((prev) => {
          const next = [...prev, activeQuest.id];
          saveProgress(next);
          return next;
        });
        setCelebrationActive(false);
        setLastCompletedReward(null);
      }, 1500);
    }
  }, [activeQuest, celebrationActive]);

  // Update context and check
  const updateContext = useCallback(
    (partial: Partial<QuestContext>) => {
      Object.assign(contextRef.current, partial);
      checkCompletion();
    },
    [checkCompletion],
  );

  // Also check on mount and when activeQuest changes
  useEffect(() => {
    // Small delay to let the UI settle
    const timer = setTimeout(checkCompletion, 200);
    return () => clearTimeout(timer);
  }, [checkCompletion]);

  const dismissWelcome = useCallback(() => {
    setWelcomeShown(true);
  }, []);

  const skipAll = useCallback(() => {
    const allIds = QUESTS.map((q) => q.id);
    setCompletedIds(allIds);
    saveProgress(allIds);
    setWelcomeShown(true);
  }, []);

  const toggleMinimize = useCallback(() => {
    setIsMinimized((prev) => {
      const next = !prev;
      saveMinimized(next);
      return next;
    });
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, "true");
    } catch {
      // ignore
    }
  }, []);

  const progress = QUESTS.length > 0 ? Math.round((completedIds.length / QUESTS.length) * 100) : 0;

  return {
    phase,
    activeQuest,
    completedIds,
    totalQuests: QUESTS.length,
    progress,
    isMinimized,
    celebrationActive,
    lastCompletedReward,
    dismissWelcome,
    skipAll,
    updateContext,
    toggleMinimize,
    dismiss,
  };
}

export { QUESTS };
